import { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Filter,
  Loader2,
  Search,
  SlidersHorizontal,
} from 'lucide-react';
import {
  INTERVENTION_PRIORITES,
  INTERVENTION_TYPES_DEMANDE,
  type InterventionStatut,
} from '@mygmao/shared';
import { api, extractErrorMessage } from '../lib/api';
import { InterventionDetailDrawer } from '../components/interventions/InterventionDetailDrawer';
import { SavedFilterMenu } from '../components/interventions/SavedFilterMenu';
import { typeDemandeLabel } from '../components/interventions/badges';

/**
 * Agenda consolidé des interventions (US client — planning).
 *
 * Vue mensuelle affichant côte à côte :
 *  - les interventions PLANIFIÉES (positionnées sur leur date planifiée) ;
 *  - les interventions RÉALISÉES / curatives rétroactives (sur leur date de
 *    fin d'intervention, donc clôturées).
 *
 * Reprend le MÊME filtrage que la page « Toutes les interventions » : onglets
 * statut, priorité, type, service, recherche, filtres avancés (technicien /
 * demandeur / prestataire) et filtres sauvegardés. La fenêtre de dates est
 * pilotée par le mois affiché. Branché sur `GET /interventions/agenda`.
 */

interface AgendaEvent {
  event_id: string;
  intervention_id: string;
  date: string;
  categorie: 'planifie' | 'realise';
  numero_demande: string;
  titre: string;
  type_demande: string;
  priorite: string;
  statut: string;
  service: { id: string; nom_complet: string } | null;
  equipement: { id: string; code_equipement: string; designation: string } | null;
}

interface ServiceOption {
  id: string;
  code: string;
  nom_complet: string;
}
interface PersonnelOption {
  id: string;
  first_name: string;
  last_name: string;
}
interface PrestataireOption {
  id: string;
  nom: string;
}

interface FiltersState {
  search: string;
  priorite: string;
  type_demande: string;
  service_demandeur_id: string;
  technicien_affecte_id: string;
  demandeur_id: string;
  prestataire_id: string;
}

const EMPTY_FILTERS: FiltersState = {
  search: '',
  priorite: '',
  type_demande: '',
  service_demandeur_id: '',
  technicien_affecte_id: '',
  demandeur_id: '',
  prestataire_id: '',
};

const TABS: Array<{ key: string; label: string; statuts: InterventionStatut[] | null }> = [
  { key: 'toutes', label: 'Toutes', statuts: null },
  { key: 'nouvelles', label: 'Nouvelles', statuts: ['nouveau'] },
  { key: 'a-planifier', label: 'À planifier', statuts: ['affecte'] },
  { key: 'en-cours', label: 'En cours', statuts: ['en_cours', 'planifie', 'en_attente_pieces'] },
  {
    key: 'en-attente-validation',
    label: 'En attente validation',
    statuts: ['en_attente_validation'],
  },
  { key: 'a-cloturer', label: 'À clôturer', statuts: ['en_attente_validation_finale'] },
  { key: 'cloturees', label: 'Clôturées', statuts: ['cloture'] },
  { key: 'annulees', label: 'Annulées', statuts: ['annule'] },
];

const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MONTHS = [
  'Janvier',
  'Février',
  'Mars',
  'Avril',
  'Mai',
  'Juin',
  'Juillet',
  'Août',
  'Septembre',
  'Octobre',
  'Novembre',
  'Décembre',
];

function mondayIndex(d: Date): number {
  return (d.getDay() + 6) % 7;
}

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

export function InterventionAgendaPage() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [events, setEvents] = useState<AgendaEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  const [filters, setFilters] = useState<FiltersState>(EMPTY_FILTERS);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [activeTab, setActiveTab] = useState('toutes');

  const [services, setServices] = useState<ServiceOption[]>([]);
  const [personnels, setPersonnels] = useState<PersonnelOption[]>([]);
  const [prestataires, setPrestataires] = useState<PrestataireOption[]>([]);

  // Grille visible : du lundi précédant le 1er au dimanche suivant le dernier jour.
  const grid = useMemo(() => {
    const first = new Date(year, month, 1);
    const gridStart = new Date(year, month, 1 - mondayIndex(first));
    const days: Date[] = [];
    for (let i = 0; i < 42; i++) {
      days.push(new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i));
    }
    return days;
  }, [year, month]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(filters.search.trim()), 300);
    return () => clearTimeout(t);
  }, [filters.search]);

  useEffect(() => {
    api
      .get<{ data: ServiceOption[] }>('/services?statut=actif&page_size=200')
      .then((res) => setServices(res.data.data))
      .catch(() => setServices([]));
    api
      .get<{ data: PersonnelOption[] }>('/personnels?status=active&page_size=200')
      .then((res) => setPersonnels(res.data.data))
      .catch(() => setPersonnels([]));
    api
      .get<{ data: PrestataireOption[] }>('/prestataires?is_active=true&page_size=200')
      .then((res) => setPrestataires(res.data.data))
      .catch(() => setPrestataires([]));
  }, []);

  useEffect(() => {
    const from = grid[0];
    const last = grid[grid.length - 1];
    if (!from || !last) return;
    const to = new Date(last.getFullYear(), last.getMonth(), last.getDate(), 23, 59, 59);

    const params = new URLSearchParams();
    params.set('from', from.toISOString());
    params.set('to', to.toISOString());
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (filters.priorite) params.set('priorite', filters.priorite);
    if (filters.type_demande) params.set('type_demande', filters.type_demande);
    if (filters.service_demandeur_id)
      params.set('service_demandeur_id', filters.service_demandeur_id);
    if (filters.technicien_affecte_id)
      params.set('technicien_affecte_id', filters.technicien_affecte_id);
    if (filters.demandeur_id) params.set('demandeur_id', filters.demandeur_id);
    if (filters.prestataire_id) params.set('prestataire_id', filters.prestataire_id);
    const currentTab = TABS.find((t) => t.key === activeTab);
    if (currentTab?.statuts) params.set('statut', currentTab.statuts.join(','));

    setLoading(true);
    setErrorMessage(null);
    api
      .get<{ data: AgendaEvent[] }>(`/interventions/agenda?${params.toString()}`)
      .then((res) => setEvents(res.data.data))
      .catch((err) => setErrorMessage(extractErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [
    grid,
    debouncedSearch,
    filters.priorite,
    filters.type_demande,
    filters.service_demandeur_id,
    filters.technicien_affecte_id,
    filters.demandeur_id,
    filters.prestataire_id,
    activeTab,
  ]);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, AgendaEvent[]>();
    for (const ev of events) {
      const key = ymd(new Date(ev.date));
      const arr = map.get(key) ?? [];
      arr.push(ev);
      map.set(key, arr);
    }
    return map;
  }, [events]);

  function updateFilter<K extends keyof FiltersState>(key: K, value: FiltersState[K]) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function applySavedFilter(payload: Record<string, unknown>) {
    setFilters({ ...EMPTY_FILTERS, ...(payload as Partial<FiltersState>) });
  }

  const currentFilters = useMemo(() => {
    const out: Record<string, unknown> = {};
    (Object.keys(filters) as Array<keyof FiltersState>).forEach((k) => {
      const v = filters[k];
      if (v !== '' && v !== null && v !== undefined) out[k] = v;
    });
    return out;
  }, [filters]);

  const activeFilterCount = Object.keys(currentFilters).filter((k) => k !== 'search').length;

  function goPrev() {
    if (month === 0) {
      setYear((y) => y - 1);
      setMonth(11);
    } else setMonth((m) => m - 1);
  }
  function goNext() {
    if (month === 11) {
      setYear((y) => y + 1);
      setMonth(0);
    } else setMonth((m) => m + 1);
  }
  function goToday() {
    setYear(today.getFullYear());
    setMonth(today.getMonth());
  }

  const todayKey = ymd(today);
  const plannedCount = events.filter((e) => e.categorie === 'planifie').length;
  const realisedCount = events.filter((e) => e.categorie === 'realise').length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 flex items-center">
            <CalendarDays className="w-5 h-5 mr-2 text-primary-600" />
            Agenda des interventions
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Planifiées et réalisées (curatif rétroactif) — vue consolidée
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={goToday}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Aujourd'hui
          </button>
          <button
            onClick={goPrev}
            className="p-1.5 border border-gray-300 rounded-lg hover:bg-gray-50"
            aria-label="Mois précédent"
          >
            <ChevronLeft className="w-4 h-4 text-gray-600" />
          </button>
          <span className="text-sm font-semibold text-gray-900 w-36 text-center">
            {MONTHS[month]} {year}
          </span>
          <button
            onClick={goNext}
            className="p-1.5 border border-gray-300 rounded-lg hover:bg-gray-50"
            aria-label="Mois suivant"
          >
            <ChevronRight className="w-4 h-4 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Onglets statut (identiques à la liste des interventions) */}
      <div className="border-b border-gray-200 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <nav className="flex">
          {TABS.map((tab) => {
            const active = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-3 py-2 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  active
                    ? 'text-primary-600 border-primary-500 bg-primary-50/50'
                    : 'text-gray-600 border-transparent hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Toolbar filtres (identique à la liste) */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <SavedFilterMenu currentFilters={currentFilters} onApply={applySavedFilter} />
            <select
              value={filters.priorite}
              onChange={(e) => updateFilter('priorite', e.target.value)}
              className={selectClass}
            >
              <option value="">Toutes priorités</option>
              {INTERVENTION_PRIORITES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            <select
              value={filters.type_demande}
              onChange={(e) => updateFilter('type_demande', e.target.value)}
              className={selectClass}
            >
              <option value="">Tous types</option>
              {INTERVENTION_TYPES_DEMANDE.map((t) => (
                <option key={t} value={t}>
                  {typeDemandeLabel(t)}
                </option>
              ))}
            </select>
            <select
              value={filters.service_demandeur_id}
              onChange={(e) => updateFilter('service_demandeur_id', e.target.value)}
              className={selectClass}
            >
              <option value="">Tous services</option>
              {services.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.code} — {s.nom_complet}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher…"
                value={filters.search}
                onChange={(e) => updateFilter('search', e.target.value)}
                className="pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg w-52 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <button
              type="button"
              onClick={() => setShowAdvanced((s) => !s)}
              className={`inline-flex items-center px-3 py-1.5 border rounded-lg text-sm font-medium ${
                showAdvanced || activeFilterCount > 0
                  ? 'border-primary-300 bg-primary-50 text-primary-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <SlidersHorizontal className="w-3.5 h-3.5 mr-1.5" />
              Plus de filtres
              {activeFilterCount > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold bg-primary-600 text-white rounded-full">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {showAdvanced && (
          <div className="border-t border-gray-100 pt-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <AdvField label="Technicien">
                <select
                  value={filters.technicien_affecte_id}
                  onChange={(e) => updateFilter('technicien_affecte_id', e.target.value)}
                  className={selectClass + ' w-full'}
                >
                  <option value="">— Tous —</option>
                  {personnels.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.last_name} {p.first_name}
                    </option>
                  ))}
                </select>
              </AdvField>
              <AdvField label="Demandeur">
                <select
                  value={filters.demandeur_id}
                  onChange={(e) => updateFilter('demandeur_id', e.target.value)}
                  className={selectClass + ' w-full'}
                >
                  <option value="">— Tous —</option>
                  {personnels.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.last_name} {p.first_name}
                    </option>
                  ))}
                </select>
              </AdvField>
              <AdvField label="Prestataire">
                <select
                  value={filters.prestataire_id}
                  onChange={(e) => updateFilter('prestataire_id', e.target.value)}
                  className={selectClass + ' w-full'}
                >
                  <option value="">— Tous —</option>
                  {prestataires.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nom}
                    </option>
                  ))}
                </select>
              </AdvField>
            </div>
            <div className="flex justify-end mt-3">
              <button
                onClick={() => setFilters(EMPTY_FILTERS)}
                className="inline-flex items-center text-xs text-gray-600 hover:text-gray-900"
              >
                <Filter className="w-3.5 h-3.5 mr-1" />
                Réinitialiser
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-4 text-xs text-gray-600">
        <span className="flex items-center">
          <span className="w-3 h-3 rounded-sm bg-blue-500 mr-1.5" />
          Planifiée ({plannedCount})
        </span>
        <span className="flex items-center">
          <span className="w-3 h-3 rounded-sm bg-green-500 mr-1.5" />
          Réalisée ({realisedCount})
        </span>
      </div>

      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
          {WEEKDAYS.map((d) => (
            <div
              key={d}
              className="px-2 py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide"
            >
              {d}
            </div>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-16">
            <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
          </div>
        ) : (
          <div className="grid grid-cols-7">
            {grid.map((day) => {
              const key = ymd(day);
              const inMonth = day.getMonth() === month;
              const dayEvents = eventsByDay.get(key) ?? [];
              return (
                <div
                  key={key}
                  className={`min-h-[96px] border-b border-r border-gray-100 p-1.5 last:border-r-0 ${
                    inMonth ? 'bg-white' : 'bg-gray-50/60'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span
                      className={`text-xs font-medium ${
                        key === todayKey
                          ? 'bg-primary-500 text-white rounded-full w-5 h-5 flex items-center justify-center'
                          : inMonth
                            ? 'text-gray-700'
                            : 'text-gray-400'
                      }`}
                    >
                      {day.getDate()}
                    </span>
                  </div>
                  <div className="space-y-1">
                    {dayEvents.slice(0, 4).map((ev) => (
                      <button
                        key={ev.event_id}
                        onClick={() => setOpenId(ev.intervention_id)}
                        title={`${ev.numero_demande} · ${ev.titre}${
                          ev.equipement ? ` · ${ev.equipement.code_equipement}` : ''
                        }`}
                        className={`w-full text-left truncate text-[11px] leading-tight px-1.5 py-1 rounded border ${
                          ev.categorie === 'planifie'
                            ? 'bg-blue-50 border-blue-200 text-blue-800 hover:bg-blue-100'
                            : 'bg-green-50 border-green-200 text-green-800 hover:bg-green-100'
                        }`}
                      >
                        <span className="font-semibold">{ev.numero_demande}</span> · {ev.titre}
                      </button>
                    ))}
                    {dayEvents.length > 4 && (
                      <p className="text-[10px] text-gray-400 pl-1">
                        +{dayEvents.length - 4} autre(s)
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {openId && (
        <InterventionDetailDrawer interventionId={openId} onClose={() => setOpenId(null)} />
      )}
    </div>
  );
}

const selectClass =
  'px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

function AdvField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      {children}
    </div>
  );
}
